/*
  # Scientific Publications Admin List Component
  
  1. Changes
    - Created admin interface for managing scientific publications
    - Added drag-and-drop reordering functionality
    - Integrated with database for CRUD operations
*/

import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, FileText, Calendar, Users } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { ScientificPublication } from '../../types/scientific-publications';
import ScientificPublicationModal from './ScientificPublicationModal';

interface SortableItemProps {
  publication: ScientificPublication;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ publication, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: publication.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      amphibians: 'bg-green-100 text-green-800',
      reptiles: 'bg-orange-100 text-orange-800',
      ecology: 'bg-blue-100 text-blue-800',
      behavior: 'bg-purple-100 text-purple-800',
      conservation: 'bg-red-100 text-red-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group p-6 bg-[#1F2B1F] rounded-xl"
    >
      <div className="flex items-start gap-4">
        <div {...attributes} {...listeners} className="cursor-grab mt-2">
          <GripVertical className="w-5 h-5 text-white/40" />
        </div>
        
        <div className="aspect-video w-32 overflow-hidden rounded-lg flex-shrink-0">
          <iframe
            src={`${publication.pdf_url}#toolbar=0&navpanes=0&scrollbar=0`}
            title={`Preview of ${publication.title}`}
            className="w-full h-full border-0 pointer-events-none"
            style={{ 
              transform: 'scale(1.5)',
              transformOrigin: 'top left',
              width: '66.67%',
              height: '66.67%'
            }}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2 line-clamp-2">{publication.title}</h3>
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mb-2">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{publication.authors.join(', ')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>{publication.journal}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{publication.year}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(publication.category)}`}>
                  {publication.category.charAt(0).toUpperCase() + publication.category.slice(1)}
                </span>
                <span className="text-xs text-white/40">Position: {index + 1}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={onEdit}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

const ScientificPublicationsList = () => {
  const [publications, setPublications] = useState<ScientificPublication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<ScientificPublication | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const { data, error } = await supabase
        .from('scientific_publications')
        .select('*')
        .order('display_order', { ascending: true })
        .order('year', { ascending: false });

      if (error) throw error;
      setPublications(data || []);
    } catch (err) {
      console.error('Error fetching publications:', err);
      setError('Failed to fetch publications');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = publications.findIndex((p) => p.id === active.id);
    const newIndex = publications.findIndex((p) => p.id === over.id);
    
    const newOrder = arrayMove(publications, oldIndex, newIndex);

    try {
      // Update display orders in database
      for (let i = 0; i < newOrder.length; i++) {
        const { error } = await supabase
          .from('scientific_publications')
          .update({ display_order: i })
          .eq('id', newOrder[i].id);

        if (error) throw error;
      }

      setPublications(newOrder);
    } catch (err) {
      console.error('Error updating publication order:', err);
      fetchPublications();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this publication?')) return;

    try {
      const { error } = await supabase
        .from('scientific_publications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setPublications(publications.filter(pub => pub.id !== id));
    } catch (err) {
      console.error('Error deleting publication:', err);
      alert('Failed to delete publication');
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Scientific Publications</h2>
        <button
          onClick={() => {
            setSelectedPublication(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Publication
        </button>
      </div>

      <div className="bg-[#1F2B1F] rounded-xl overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={publications}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y divide-white/10">
              {publications.length === 0 ? (
                <div className="p-8 text-center text-white/50">No publications found</div>
              ) : (
                <div className="space-y-4 p-4">
                  {publications.map((publication, index) => (
                    <SortableItem
                      key={publication.id}
                      publication={publication}
                      index={index}
                      onEdit={() => {
                        setSelectedPublication(publication);
                        setIsModalOpen(true);
                      }}
                      onDelete={() => handleDelete(publication.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <ScientificPublicationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPublication(null);
        }}
        publication={selectedPublication}
        onSave={fetchPublications}
      />
    </div>
  );
};

export default ScientificPublicationsList;